// =============================================================================
// NotificationBell — header bell with unread badge + recent inbox dropdown
// =============================================================================
// Polls the unread count on an interval (cheap COUNT query) and lazily loads
// the most-recent inbox items only while the panel is open. Marking an item
// read invalidates the inbox queries. The full history lives at
// `/notifications`. Open state is owned here (not by a shared dropdown) so the
// recent query can be gated precisely on visibility.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import {
  notificationKeys,
  notificationHref,
  timeAgo,
  UNREAD_POLL_INTERVAL_MS,
  type InboxResponse,
  type InboxNotification,
  type UnreadCountResponse,
} from '@/lib/notifications';
import { NotificationBody } from './NotificationBody';

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unreadQuery = useQuery<UnreadCountResponse, Error>({
    queryKey: notificationKeys.unreadCount,
    queryFn: () =>
      api.get<UnreadCountResponse>('/api/v1/notifications/unread-count'),
    refetchInterval: UNREAD_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const recentQuery = useQuery<InboxResponse, Error>({
    queryKey: notificationKeys.recent,
    queryFn: () =>
      api.get<InboxResponse>('/api/v1/notifications?page=1&limit=8'),
    enabled: open,
    staleTime: 10_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = useMutation<unknown, Error, string>({
    mutationFn: (id) => api.post(`/api/v1/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation<unknown, Error, void>({
    mutationFn: () => api.post('/api/v1/notifications/read-all', {}),
    onSuccess: invalidate,
  });

  const unread = unreadQuery.data?.count ?? 0;
  const items = recentQuery.data?.items ?? [];

  const handleOpenItem = (n: InboxNotification) => {
    if (!n.readAt) markRead.mutate(n.id);
    const href = notificationHref(n);
    setOpen(false);
    navigate(href ?? '/notifications');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        className="relative rounded-lg p-2 text-text-secondary transition-colors hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
      >
        <BellIcon />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[360px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="text-sm font-semibold text-text-primary">
              Notifications
            </p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[22rem] overflow-y-auto">
            {recentQuery.isLoading ? (
              <p className="px-4 py-8 text-center text-sm text-text-tertiary">
                Loading…
              </p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-text-tertiary">
                You're all caught up.
              </p>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`flex w-full gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-secondary ${
                    n.readAt ? '' : 'bg-brand-50/40'
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      n.readAt ? 'bg-transparent' : 'bg-brand-500'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => handleOpenItem(n)}
                      className="block w-full text-left text-sm font-medium text-text-primary hover:text-brand-700"
                    >
                      {n.title}
                    </button>
                    <NotificationBody
                      notification={n}
                      className="mt-0.5 text-xs"
                      onInteract={() => {
                        if (!n.readAt) markRead.mutate(n.id);
                      }}
                    />
                    <span className="mt-1 block text-[11px] text-text-tertiary">
                      {timeAgo(n.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-border px-4 py-2.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              className="w-full text-center text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
      />
    </svg>
  );
}
